// StateCandidates.js
import React, { useMemo, useState } from 'react';
import { Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { Search } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import useCandidates from '../../hooks/useCandidates';
import UseHandleContainerClickCandidate from './UseHandleContainerClickCandidate';

import '../feed/FeedItem.css';
import './StateCandidates.css';

/**
 * StateCandidates
 *
 * Candidate list for one state and office, rendered as FeedItem cards.
 *
 * Replaces the legacy GenerateFeedStateRepsAll / CreateFeedItemStateRepsForPage
 * pair, which rendered through ReactDOM.createPortal into elements looked up by
 * document.getElementById, saved to localStorage by hand, and called
 * window.location.reload() afterwards. This renders normally, themes from
 * FeedItem.css, and navigates to /us/candidate/{candidate_id}.
 *
 * @param {Object} props
 * @param {string} props.stateAbbreviation - Two letter code. Omit for President.
 * @param {string} props.candidateType - 'House' | 'Senate' | 'President'
 * @param {boolean} props.showSearch - Render the filter input. Default true.
 * @param {number} props.initialCount - How many to show before "More".
 */

const PAGE_SIZE = 10;

// The FEC publishes names inverted: 'PALMER, GARY'. Flip when there is exactly
// one comma - rows carrying suffixes or committee names are left alone.
export const displayCandidateName = (name) => {
  if (!name) return '';

  const parts = name.split(',');
  if (parts.length !== 2) return name.trim();

  const [surname, given] = parts.map((part) => part.trim());
  if (!given) return surname;

  return `${given} ${surname}`;
};

const formatDate = (inputDate) => {
  if (!inputDate) return null;

  const date = new Date(inputDate);
  if (Number.isNaN(date.getTime())) return null;

  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return `${utcDate.getMonth() + 1}-${utcDate.getDate()}-${utcDate.getFullYear()}`;
};

function StateCandidates({
  stateAbbreviation,
  candidateType = 'House',
  showSearch = true,
  initialCount = PAGE_SIZE,
}) {
  const { t } = useTranslation('common');
  const openCandidate = UseHandleContainerClickCandidate();

  const { candidates, loading, error } = useCandidates(stateAbbreviation, candidateType);

  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(initialCount);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return candidates;

    return candidates.filter((candidate) => {
      const haystack = [
        candidate.name,
        candidate.party_full,
        candidate.state,
        candidate.district ? `district ${candidate.district}` : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(needle);
    });
  }, [candidates, query]);

  const visible = filtered.slice(0, visibleCount);

  if (loading) {
    return (
      <div className="state-candidates-status">
        <Spinner id="spinner" animation="grow" variant="light" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="member-detail-text">
        {t('common:errorPrefix', 'Error: {{message}}', { message: error.message })}
      </p>
    );
  }

  if (candidates.length === 0) {
    return (
      <p className="member-detail-text">
        {t('common:noCandidatesFound', 'No candidates on file for this office yet.')}
      </p>
    );
  }

  return (
    <div className="state-candidates">
      {showSearch && (
        <Form
          className="state-candidates-search"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Form.Group className="d-flex w-100">
            <Form.Control
              id="searchInput"
              type="text"
              placeholder={t('common:searchCandidates', 'Search candidates')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setVisibleCount(initialCount);
              }}
              className="flex-grow-1"
            />
            <Button id="ggButton" type="submit" aria-label={t('common:search', 'Search')}>
              <Search />
            </Button>
          </Form.Group>
        </Form>
      )}

      {visible.length === 0 ? (
        <p className="member-detail-text">
          {t('common:noResultsFound', 'No results found.')}
        </p>
      ) : (
        visible.map((candidate) => {
          const name = displayCandidateName(candidate.name);
          const filingDate = formatDate(candidate.last_file_date);

          // Assembled by filtering so a missing party or district never leaves a
          // dangling separator.
          const meta = [
            candidateType !== 'President' ? candidate.state : null,
            candidate.party_full,
            candidateType === 'House' && candidate.district
              ? `${t('common:district', 'District')} ${candidate.district}`
              : null,
          ].filter(Boolean);

          return (
            <div className="feed-item feed-item--default mb-4" key={candidate.candidate_id}>
              <Card>
                <Card.Title
                  id="billText"
                  className="card-title p-3"
                  onClick={() => openCandidate(candidate.candidate_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openCandidate(candidate.candidate_id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                >
                  {name}
                </Card.Title>

                <Card.Body id="billBodyText">
                  {candidate.office_full} {t('common:candidateSuffix', '(Candidate)')}
                </Card.Body>

                {meta.length > 0 && (
                  <Card.Body id="billBodyText">{meta.join(' · ')}</Card.Body>
                )}

                {filingDate && (
                  <Card.Body id="billBodyText">
                    {t('common:filingDate', 'Filing date: {{date}}', { date: filingDate })}
                  </Card.Body>
                )}

          
              </Card>
            </div>
          );
        })
      )}

      {visibleCount < filtered.length && (
        <div className="state-candidates-more">
          <Button
            id="standardButton"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          >
            {t('common:more', 'More')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default StateCandidates;